import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  useMemberAsMember,
  useMemberInvoiceAsMember,
  useMemberPaymentMethodListAsMember,
  useMutationLateCancelAsMember,
  useMutationPayInvoiceAsMember,
  useQueryRulesAsPublic,
} from "@gymflow/api";
import { cn, formatCurrency, rulesHelper } from "@gymflow/helpers";
import { useQuery } from "@tanstack/react-query";
import { usePortalRoutes } from "apps/portal/src/hooks";
import useScaModal from "apps/portal/src/hooks/useScaModal";
import { useClubSettings } from "apps/portal/src/providers";
import { AuthenticationContext } from "apps/portal/src/providers/authentication/AuthenticationContext";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import { RouteFeature } from "apps/portal/src/routes";
import useGymflowModels from "apps/portal/src/store";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { DateTime } from "luxon";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { ApiResolverContext } from "../../../providers/ApiResolverProvider/ApiResolverContext";
import { Button, ErrorMessage } from "../../atoms";
import { PaymentMethodPicker } from "../../molecules/PaymentMethodPicker";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import useRedirectIfNotAuthenticated from "../useRedirectIfNotAuthenticated";
import { getTimetableQueryParams } from "./components/useTimetableQueryParamsStore";

export type HostedPagesClassLateCancelCheckoutProps = {};

const HostedPagesClassLateCancelCheckout =
  ({}: HostedPagesClassLateCancelCheckoutProps) => {
    const { isEmbed, createSiteOrEmbedLink } = usePortalRoutes();
    const { t } = useTranslation();
    const [currentInvoiceId, setCurrentInvoiceId] = useState<string>();
    const showScaModal = useScaModal({
      asMember: true,
    });
    const { userId: id } = useContext(AuthenticationContext);

    useRedirectIfNotAuthenticated(
      createSiteOrEmbedLink(
        `${RouteFeature.SiteTimetable}?${getTimetableQueryParams()}`,
      ),
    );
    const { api } = useGymflowModels();
    const { club } = useClubSettings();
    const { clubId } = useContext(ApiResolverContext);
    const { data: userMember } = useMemberAsMember({
      api,
      memberId: id,
      tz: club.timezone,
    });
    const { eventOccurrenceId } = useParams<{ eventOccurrenceId: string }>();
    const [currentPaymentMethodId, setCurrentPaymentMethodId] =
      useState<string>();

    const { data: invoice } = useMemberInvoiceAsMember({
      api,
      invoiceNumber: currentInvoiceId,
    });

    const { mutateAsync: payFailed } = useMutationPayInvoiceAsMember({
      api,
    });

    const { data: eventOccurrence } = useQuery(
      apiV2.member.eventOccurrence.queries.id(Number(eventOccurrenceId)),
    );

    const { mutateAsync: mutateAsyncLateCancel } =
      useMutationLateCancelAsMember({ api });

    const { toast } = useContext(ToastContext);
    const onPay = async () => {
      if (!eventOccurrence?.rsvpId) {
        return;
      }

      const result = await mutateAsyncLateCancel({
        rsvpId: eventOccurrence.rsvpId,
        paymentMethodId: currentPaymentMethodId,
      });

      const invoiceNumber = result.invoiceNumber;

      setCurrentInvoiceId(invoiceNumber);
      if (invoiceNumber) {
        showScaModal({
          invoiceNumber,
        });
      }
    };

    const history = useHistory();
    useEffect(() => {
      if (invoice?.status === "PAID" || invoice?.status === "PROCESSING") {
        toast({
          message: t("pages.hostedPagesCheckout.invoicePaid"),
          description: t("pages.hostedPagesCheckout.invoicePaidDescription"),
          intent: "success",
        });
        setTimeout(() => {
          history.goBack();
        }, 1500);
      }
    }, [history, invoice?.status, t, toast]);
    const [parent] = useAutoAnimate();
    const { status: paymentsLoadingStatus } =
      useMemberPaymentMethodListAsMember({
        api,
        clubId,
        memberId: id,
      });
    const { data: activityRules } = useQueryRulesAsPublic({
      api,
      params: eventOccurrence
        ? {
            activityId: eventOccurrence?.activityId,
          }
        : undefined,
    });
    const rules = rulesHelper.mapRuleGroupToClassRules(activityRules);
    const arePaymentMethodsLoading =
      !paymentsLoadingStatus || paymentsLoadingStatus === "pending";
    return (
      <div
        className={cn(
          "track-height flex w-full flex-col items-center overflow-y-auto bg-gray-50 dark:bg-gray-800",
          {
            "h-full": !isEmbed,
            "h-fit": isEmbed,
          },
        )}
      >
        <HostedPagesBackHeader
          label={t("pages.hostedPagesClassesCheckout.cancelBooking")}
        />
        <div
          ref={parent}
          className="flex h-fit w-full max-w-lg flex-col gap-4 px-4 py-6"
        >
          {invoice && (
            <div className="flex w-full flex-col items-center gap-y-3">
              <div className="dark:text-gray-0 text-3xl font-bold text-gray-950">
                {formatCurrency(invoice.totalAmount, club.defaultCurrency)}
              </div>
              <div className="flex flex-col items-center gap-1">
                {(invoice.status === "PAID" ||
                  invoice.status === "PROCESSING") && (
                  <>
                    <div className="text-success-600 dark:text-success-400 text-center font-bold">
                      Payment Successful
                    </div>
                    <div className="font-medium text-gray-600 dark:text-gray-400">
                      {DateTime.fromISO(
                        invoice.issueDate ?? new Date().toISOString(),
                      ).toLocaleString(DateTime.DATE_MED)}
                    </div>
                  </>
                )}
                {invoice.status === "PAST_DUE" && (
                  <div className="flex flex-col items-center gap-3">
                    <div className="text-error-600 dark:text-error-400 text-center font-bold">
                      Payment Failed
                    </div>
                    <div className="font-medium text-gray-600 dark:text-gray-400">
                      <Button
                        onClick={async () => {
                          const newInvoice = await payFailed({
                            invoiceNumber: invoice.number,
                            paymentMethodId: currentPaymentMethodId,
                          });
                          showScaModal({
                            invoiceNumber: newInvoice.number,
                          });
                        }}
                      >
                        Retry Payment
                      </Button>
                    </div>
                  </div>
                )}
                {invoice.status === "AWAITING_AUTHORIZATION" && (
                  <div className="text-warning-600 text-center font-bold">
                    Payment Awaiting Authorization
                  </div>
                )}
              </div>
            </div>
          )}
          {userMember && (
            <div className="max-w-full">
              <PaymentMethodPicker
                userMemberId={userMember.id}
                currentPaymentMethodId={currentPaymentMethodId}
                onChange={setCurrentPaymentMethodId}
                disabled={!!invoice}
                allowBankPayment
                asMember
              />
            </div>
          )}
          <div className="flex w-full border-b-2 border-dashed border-gray-400 dark:border-gray-600" />
          <div className="flex w-full flex-col gap-2">
            {rules.bookingCancellationRule && (
              <div className="flex w-full flex-row justify-between">
                <div className="font-medium text-gray-500">
                  {t("pages.hostedPagesClassesCheckout.lateCancelationFee")}
                </div>
                <div className="font-medium text-gray-500">
                  {formatCurrency(
                    rules.bookingCancellationRule.cancellationFee,
                    club.defaultCurrency,
                  )}
                </div>
              </div>
            )}

            {rules.bookingCancellationRule && (
              <div className="dark:text-gray-0 flex w-full flex-row items-center justify-between text-gray-950">
                <div className="text-xl font-bold">
                  {t("pages.hostedPagesClassesCheckout.amountDue")}
                </div>
                <div className="text-xl font-bold">
                  {formatCurrency(
                    rules.bookingCancellationRule.cancellationFee,
                    club.defaultCurrency,
                  )}
                </div>
              </div>
            )}
          </div>
          {!invoice && (
            <div className="flex w-full flex-col gap-1">
              {!arePaymentMethodsLoading && !currentPaymentMethodId && (
                <ErrorMessage text="Please select a payment method to continue" />
              )}
              <Button
                intent="secondary"
                className="w-full"
                disabled={
                  !rules.bookingCancellationRule || !currentPaymentMethodId
                }
                onClick={onPay}
              >
                {`Pay ${
                  rules.bookingCancellationRule
                    ? formatCurrency(
                        rules.bookingCancellationRule.cancellationFee,
                        club.defaultCurrency,
                      )
                    : ""
                }
                `}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

export default HostedPagesClassLateCancelCheckout;
